export type TimelineNode = {
  date: string;
  title: string;
  content: string;
};

export type Discussion = {
  user: string;
  role: string;
  time: string;
  content: string;
  likes: number;
};

export type EventItem = {
  id: string;
  title: string;
  categoryId: string;
  categoryName: string;
  tags: string[];
  summary: string;
  intro: string;
  heat: number;
  comments: number;
  date: string;
  status: "持续跟进" | "已回应" | "已归档";
  cover: string;
  timeline: TimelineNode[];
  discussions: Discussion[];
  notes: string[];
};

export const events: EventItem[] = [
  {
    id: "live-schedule-change",
    title: "深夜直播临时改档：一次排期调整引发的讨论",
    categoryId: "live",
    categoryName: "直播圈",
    tags: ["排期调整", "粉丝沟通", "开播预告"],
    summary:
      "某人气直播间在预告后临时把开播时段调整到深夜，前后口径并不一致，观众在评论区展开讨论，随后运营团队发布排期说明。",
    intro:
      "直播排期看起来只是时间表上的几行字，但对长期追播的观众来说，它意味着「几点守在屏幕前」。这一次的临时调整之所以被反复讨论，是因为前后两次公布的时间并不吻合，而补充说明又晚了一步。我们把整个过程的公开信息按时间顺序整理出来，方便你判断讨论的焦点究竟在哪里。",
    heat: 9821,
    comments: 1284,
    date: "2026-09-06",
    status: "已回应",
    cover:
      "https://readdy.ai/api/search-image?query=Abstract%20editorial%20illustration%20of%20glowing%20microphone%20silhouette%20among%20warm%20coral%20and%20amber%20geometric%20arcs%20on%20cream%20paper%20background%2C%20soft%20film%20grain%20texture%2C%20minimal%20modern%20magazine%20art%20style%2C%20no%20text%2C%20no%20faces%2C%20calm%20balanced%20composition&width=800&height=600&seq=gt-cover-live-01&orientation=landscape",
    timeline: [
      {
        date: "09-04",
        title: "排期预告发布",
        content: "账号主页更新本周直播排期表，注明周四晚黄金时段开播，并附带当期主题。",
      },
      {
        date: "09-05",
        title: "时段临时调整",
        content: "开播前数小时，页面排期被更换为深夜时段，原定时段未做同步说明，评论区开始出现追问。",
      },
      {
        date: "09-06",
        title: "运营团队发布说明",
        content: "团队解释为设备调试与档期协调，公布调整后的排期表，并承诺后续变更提前公示。",
      },
      {
        date: "09-07",
        title: "补播如期进行",
        content: "补播场次按新排期正常开播，话题热度回落，讨论重心转回内容本身。",
      },
    ],
    discussions: [
      {
        user: "瓜田小满",
        role: "长期追播观众",
        time: "2 小时前",
        content: "其实改档本身能理解，介意的是改了不说。提前一句话说明，大家就不会在评论区互相猜。",
        likes: 342,
      },
      {
        user: "夜班看播人",
        role: "普通观众",
        time: "5 小时前",
        content: "深夜档对我反而更友好，但排期表两种版本同时挂着确实容易让人误会。",
        likes: 187,
      },
      {
        user: "内容观察员阿吉",
        role: "话题整理者",
        time: "昨天",
        content: "这类讨论的关键从来不是几点开播，而是信息发布是否同步。口径一致比时间本身更重要。",
        likes: 256,
      },
    ],
    notes: [
      "排期类话题的争议点通常集中在「变更是否同步公示」，而非变更本身。",
      "事件整理依据账号公开更新与团队说明，不含任何非公开信息。",
    ],
  },
  {
    id: "short-video-lip-sync",
    title: "热门短视频对口型争议：二创与原创的边界讨论",
    categoryId: "short",
    categoryName: "短视频",
    tags: ["二创", "版权意识", "创作规范"],
    summary:
      "一条热度很高的对口型短视频被指与原音频创作者标注习惯不同，引出关于二创标注规范的讨论，双方先后做出说明。",
    intro:
      "二次创作是短视频生态里最常见的内容形式之一，它让一段音频获得新的表达，也让标注与署名成为绕不开的话题。本次讨论并不涉及归属纠纷的最终结论，而是围绕「怎样标注才算清楚」展开，许多创作者也借此分享了自己的标注习惯。",
    heat: 8634,
    comments: 976,
    date: "2026-09-02",
    status: "已归档",
    cover:
      "https://readdy.ai/api/search-image?query=Abstract%20editorial%20illustration%20of%20play%20button%20geometry%20layered%20with%20warm%20coral%20circles%20and%20amber%20waves%20on%20cream%20paper%20background%2C%20soft%20film%20grain%20texture%2C%20minimal%20modern%20poster%20style%2C%20no%20text%2C%20no%20faces%2C%20balanced%20composition&width=800&height=600&seq=gt-cover-short-02&orientation=landscape",
    timeline: [
      {
        date: "08-30",
        title: "视频走红",
        content: "一条对口型短视频进入热门推荐，评论区开始出现关于原音频出处的询问。",
      },
      {
        date: "09-01",
        title: "原音频创作者回应",
        content: "原音频作者在个人页面说明自己的标注习惯，并希望二创内容能够保留来源信息。",
      },
      {
        date: "09-02",
        title: "二创方补充说明",
        content: "视频作者发布说明，表示已补充来源标注，并分享了素材整理流程。",
      },
      {
        date: "09-03",
        title: "讨论转向规范",
        content: "话题从单条视频扩展到二创标注的通用做法，多位创作者加入经验分享。",
      },
    ],
    discussions: [
      {
        user: "剪辑台前排",
        role: "视频创作者",
        time: "3 小时前",
        content: "我的习惯是简介第一行就写音频出处，麻烦三秒，但省掉后面所有沟通成本。",
        likes: 401,
      },
      {
        user: "听歌不记名",
        role: "音乐爱好者",
        time: "8 小时前",
        content: "很多好听的音频就是因为没人标注，火了一阵就找不到了，标注其实是在帮内容活得更久。",
        likes: 233,
      },
    ],
    notes: [
      "讨论聚焦标注规范，不涉及任何一方归属的最终认定。",
      "站内不提供音频文件，仅整理公开的说明内容。",
    ],
  },
  {
    id: "live-commerce-price",
    title: "带货专场价格前后不一：优惠机制该怎么讲清",
    categoryId: "commerce",
    categoryName: "电商带货",
    tags: ["优惠机制", "专场", "消费提示"],
    summary:
      "一场带货专场在预热与正式开播阶段给出的到手价口径不同，观众比对截图后提出疑问，运营方随后补充了优惠叠加规则。",
    intro:
      "优惠券、限时折扣、平台补贴叠加在一起时，最终到手价往往需要算一遍才知道。这次讨论的核心不是价格高低，而是「预热口径」与「实际结算」之间的落差应该如何解释清楚。我们按公开信息把优惠规则的调整过程梳理成时间线。",
    heat: 7742,
    comments: 864,
    date: "2026-08-29",
    status: "已回应",
    cover:
      "https://readdy.ai/api/search-image?query=Abstract%20editorial%20illustration%20of%20shopping%20bag%20outline%20with%20warm%20coral%20and%20amber%20abstract%20shapes%20on%20cream%20paper%20background%2C%20soft%20film%20grain%20texture%2C%20minimal%20modern%20magazine%20art%2C%20no%20text%2C%20no%20faces&width=800&height=600&seq=gt-cover-shop-03&orientation=landscape",
    timeline: [
      {
        date: "08-26",
        title: "专场预热",
        content: "预热海报标注部分商品的预计到手价，并说明需叠加平台券。",
      },
      {
        date: "08-28",
        title: "开播后的差异",
        content: "部分观众发现叠加顺序不同会导致最终价格差异，评论区开始比对下单截图。",
      },
      {
        date: "08-29",
        title: "规则补充说明",
        content: "运营方发布优惠叠加示意，并注明各券的适用范围与生效顺序。",
      },
    ],
    discussions: [
      {
        user: "会算账的柚子",
        role: "消费者",
        time: "4 小时前",
        content: "到手价最好直接给一张叠加示意图，写在角落的小字真的很容易看漏。",
        likes: 298,
      },
      {
        user: "理性下单组",
        role: "消费者",
        time: "1 天前",
        content: "我下单前都会把优惠券页面重新看一遍，这次规则补充后确实清楚多了。",
        likes: 152,
      },
    ],
    notes: [
      "整理内容仅涉及公开的优惠规则说明，不构成任何购买建议。",
      "价格与库存以平台页面实时信息为准。",
    ],
  },
  {
    id: "variety-editing",
    title: "综艺正片剪辑顺序调整：观众为何执着于完整版",
    categoryId: "variety",
    categoryName: "综艺娱乐",
    tags: ["正片剪辑", "加更", "观看体验"],
    summary:
      "一档综艺的正片与会员加更版在片段顺序上存在差异，观众对比后发现部分互动被调整位置，节目组随后说明剪辑思路。",
    intro:
      "一档节目的正片、加更版、花絮往往由不同版本组成，剪辑逻辑也不完全相同。当同一段互动在两个版本中位置不同，观众很容易产生「是不是漏看了」的疑问。本次讨论更像是一次关于版本说明的公开交流。",
    heat: 6510,
    comments: 712,
    date: "2026-08-24",
    status: "已回应",
    cover:
      "https://readdy.ai/api/search-image?query=Abstract%20editorial%20illustration%20of%20stage%20curtain%20arcs%20in%20warm%20coral%20and%20amber%20tones%20over%20cream%20paper%20background%2C%20soft%20film%20grain%20texture%2C%20minimal%20modern%20editorial%20poster%20style%2C%20no%20text%2C%20no%20faces&width=800&height=600&seq=gt-cover-variety-04&orientation=landscape",
    timeline: [
      {
        date: "08-22",
        title: "正片上线",
        content: "当期正片播出，节目中段的互动片段时长较短。",
      },
      {
        date: "08-23",
        title: "加更版放出",
        content: "会员加更版收录了更完整的互动内容，位置与正片不同，观众开始比对差异。",
      },
      {
        date: "08-24",
        title: "剪辑说明",
        content: "节目组说明不同版本面向不同观看场景，并公布后续的版本更新节奏。",
      },
    ],
    discussions: [
      {
        user: "追综日记",
        role: "观众",
        time: "6 小时前",
        content: "两个版本都看一遍其实挺累的，如果片尾能提示一下差异片段会友好很多。",
        likes: 265,
      },
      {
        user: "周末沙发党",
        role: "观众",
        time: "2 天前",
        content: "加更版的节奏我更喜欢，正片更适合没时间的观众，其实定位不同。",
        likes: 143,
      },
    ],
    notes: ["站内不提供任何节目资源，仅整理公开的版本说明。", "讨论围绕观看体验展开，不针对具体个人。"],
  },
  {
    id: "social-privacy-topic",
    title: "账号归属讨论：个人信息该如何被尊重",
    categoryId: "social",
    categoryName: "社交平台",
    tags: ["个人信息", "账号安全", "网络礼仪"],
    summary:
      "围绕某社交账号的归属问题出现了多种说法，平台同步上线账号安全提示，提醒用户保护个人信息与登录凭证。",
    intro:
      "网络讨论里最需要克制的一类话题，就是涉及个人身份与账号归属的猜测。本次事件中，我们刻意隐去所有可识别的个人信息，只保留平台公开的安全提示与讨论礼仪建议，希望围观的同时也不越界。",
    heat: 5890,
    comments: 604,
    date: "2026-08-19",
    status: "已归档",
    cover:
      "https://readdy.ai/api/search-image?query=Abstract%20editorial%20illustration%20of%20overlapping%20speech%20bubbles%20in%20warm%20coral%20and%20sage%20teal%20on%20cream%20paper%20background%2C%20soft%20film%20grain%20texture%2C%20minimal%20modern%20magazine%20art%20style%2C%20no%20text%2C%20no%20faces&width=800&height=600&seq=gt-cover-social-05&orientation=landscape",
    timeline: [
      {
        date: "08-17",
        title: "话题出现",
        content: "某社交账号的动态引发关注，评论区出现关于账号归属的多种猜测。",
      },
      {
        date: "08-18",
        title: "平台安全提示",
        content: "平台发布账号安全提醒，建议用户开启二次验证、定期更换密码。",
      },
      {
        date: "08-19",
        title: "讨论降温",
        content: "话题逐步转向账号安全常识分享，相关猜测内容得到清理。",
      },
    ],
    discussions: [
      {
        user: "隐私保护手册",
        role: "内容整理者",
        time: "1 天前",
        content: "围观可以，但不要顺着线索去拼凑个人信息。讨论到人以外的部分，才算守住边界。",
        likes: 512,
      },
      {
        user: "设置两级验证",
        role: "普通用户",
        time: "2 天前",
        content: "趁这次把二次验证开了，比什么都实在。",
        likes: 221,
      },
    ],
    notes: [
      "本站不呈现任何个人身份信息、联系方式或私密内容。",
      "鼓励理性围观，拒绝人肉与猜测式传播。",
    ],
  },
  {
    id: "music-festival-order",
    title: "音乐节演出顺序调整：现场节奏与观众期待",
    categoryId: "music",
    categoryName: "音乐现场",
    tags: ["演出顺序", "现场体验", "主办说明"],
    summary:
      "一场音乐节的演出顺序在开场后做了调整，部分观众的等候时间发生变化，主办方现场广播并同步公示了新的时间表。",
    intro:
      "音乐节的顺序调整多半与现场设备、天气或行程衔接有关。对观众来说，真正影响的是一整天的等待安排。这次主办方的处理方式被不少观众认可，我们把现场公开信息整理出来，供后续活动参考。",
    heat: 4920,
    comments: 448,
    date: "2026-08-15",
    status: "已回应",
    cover:
      "https://readdy.ai/api/search-image?query=Abstract%20editorial%20illustration%20of%20sound%20wave%20lines%20in%20warm%20amber%20and%20coral%20over%20cream%20paper%20background%2C%20soft%20film%20grain%20texture%2C%20minimal%20modern%20concert%20poster%20style%2C%20no%20text%2C%20no%20faces&width=800&height=600&seq=gt-cover-music-06&orientation=landscape",
    timeline: [
      {
        date: "08-14",
        title: "开场前公示",
        content: "主办方在入场处公布全天演出时间表，并提示中场休息安排。",
      },
      {
        date: "08-15",
        title: "现场顺序微调",
        content: "因设备调试，两组演出顺序互换，现场通过广播与屏幕同步通知。",
      },
      {
        date: "08-15",
        title: "时间表更新",
        content: "更新版时间表张贴在主要通道，并同步到官方账号。",
      },
    ],
    discussions: [
      {
        user: "露营区前排",
        role: "现场观众",
        time: "3 天前",
        content: "广播通知还算及时，我提前收到消息就去补给区休息了，体验没受太大影响。",
        likes: 176,
      },
      {
        user: "键盘乐评",
        role: "乐迷",
        time: "3 天前",
        content: "顺序调整能接受，重要的是信息同步的速度，这次做得不错。",
        likes: 132,
      },
    ],
    notes: ["整理内容基于主办方公开广播与现场公示信息。", "不含任何演出资源与票务信息。"],
  },
  {
    id: "team-split-rumor",
    title: "人气组合成员动向猜想：当传闻跑在官宣前面",
    categoryId: "variety",
    categoryName: "综艺娱乐",
    tags: ["传闻", "官宣节奏", "粉丝心态"],
    summary:
      "关于某人气组合成员后续安排的传闻在社交平台扩散，官方账号随后发布常规运营说明，提醒以正式信息为准。",
    intro:
      "传闻总是比官宣跑得快，这是内容生态里反复出现的现象。本次事件中，官方并未针对传闻逐条回应，而是重申了信息发布渠道。我们整理这段过程，也是想说明「等待官宣」本身是一种更省心的围观方式。",
    heat: 4380,
    comments: 392,
    date: "2026-08-10",
    status: "已归档",
    cover:
      "https://readdy.ai/api/search-image?query=Abstract%20editorial%20illustration%20of%20five%20abstract%20circles%20arranged%20in%20a%20row%20in%20warm%20coral%20amber%20and%20teal%20on%20cream%20paper%20background%2C%20soft%20film%20grain%20texture%2C%20minimal%20modern%20editorial%20art%2C%20no%20text%2C%20no%20faces&width=800&height=600&seq=gt-cover-team-07&orientation=landscape",
    timeline: [
      {
        date: "08-08",
        title: "传闻扩散",
        content: "社交平台出现关于后续安排的多种说法，来源多为二手转述。",
      },
      {
        date: "08-09",
        title: "官方重申渠道",
        content: "官方账号发布常规运营说明，提示所有安排以正式渠道公告为准。",
      },
      {
        date: "08-10",
        title: "热度回落",
        content: "缺乏后续信息支撑，相关讨论逐渐减少。",
      },
    ],
    discussions: [
      {
        user: "只看官宣",
        role: "粉丝",
        time: "4 天前",
        content: "现在的原则很简单：没有正式公告的消息，一律当作还没发生。",
        likes: 289,
      },
      {
        user: "吃瓜但要冷静",
        role: "普通网友",
        time: "4 天前",
        content: "传闻传播链条越长，失真的概率越高，等一等真的不会错过什么。",
        likes: 164,
      },
    ],
    notes: ["本站不复述未经证实的传闻内容。", "所有整理均以公开渠道信息为依据。"],
  },
  {
    id: "brand-collab-pull",
    title: "品牌联名下架风波：合作节奏与沟通成本",
    categoryId: "commerce",
    categoryName: "电商带货",
    tags: ["联名", "下架", "商务沟通"],
    summary:
      "一次品牌联名周边在上架后不久被下架，官方表示因生产与排期衔接问题，已下单用户将获得退款与补偿说明。",
    intro:
      "联名周边往往牵涉生产、仓储、物流多方协作，任何一环延误都可能影响上架节奏。这次事件的处理重点是售后说明是否清楚，我们把公开的公告与用户反馈整理成一条清晰的时间线。",
    heat: 3960,
    comments: 358,
    date: "2026-08-04",
    status: "已回应",
    cover:
      "https://readdy.ai/api/search-image?query=Abstract%20editorial%20illustration%20of%20abstract%20tag%20label%20shapes%20in%20warm%20coral%20and%20amber%20over%20cream%20paper%20background%2C%20soft%20film%20grain%20texture%2C%20minimal%20modern%20magazine%20art%20style%2C%20no%20text%2C%20no%20faces&width=800&height=600&seq=gt-cover-brand-08&orientation=landscape",
    timeline: [
      {
        date: "08-01",
        title: "联名上架",
        content: "联名周边在指定渠道上架，页面标注预计发货时间。",
      },
      {
        date: "08-03",
        title: "商品下架",
        content: "页面显示商品已下架，已下单用户收到状态变更通知。",
      },
      {
        date: "08-04",
        title: "官方公告",
        content: "品牌方说明衔接问题，公布退款流程与补偿方案。",
      },
    ],
    discussions: [
      {
        user: "周边收藏者",
        role: "消费者",
        time: "6 天前",
        content: "公告写得挺清楚，退款流程也给了入口，比起拖着不回应好太多。",
        likes: 201,
      },
      {
        user: "下单前先看公告",
        role: "消费者",
        time: "6 天前",
        content: "预售类商品我还是会留意预计发货时间，心里有个预期就不会觉得被放鸽子。",
        likes: 118,
      },
    ],
    notes: ["退款与补偿细节以品牌方公告为准。", "本站不提供任何商品销售入口。"],
  },
  {
    id: "platform-algorithm",
    title: "平台推荐机制讨论：创作者为何关心流量来源",
    categoryId: "social",
    categoryName: "社交平台",
    tags: ["推荐机制", "创作者生态", "内容分发"],
    summary:
      "一批创作者在社区讨论推荐流量的来源结构，平台随后更新创作者后台的数据说明，补充了指标口径的注释。",
    intro:
      "当创作者看不到流量从哪来，就容易把波动归因于「被限流」。这次平台在后台补充了指标口径注释，让「推荐」「关注」「搜索」几个来源变得可区分。对内容从业者来说，这比一句解释更有价值。",
    heat: 3540,
    comments: 326,
    date: "2026-07-28",
    status: "已回应",
    cover:
      "https://readdy.ai/api/search-image?query=Abstract%20editorial%20illustration%20of%20abstract%20node%20network%20with%20warm%20amber%20and%20sage%20teal%20dots%20on%20cream%20paper%20background%2C%20soft%20film%20grain%20texture%2C%20minimal%20modern%20data%20poster%20style%2C%20no%20text%2C%20no%20faces&width=800&height=600&seq=gt-cover-algo-09&orientation=landscape",
    timeline: [
      {
        date: "07-26",
        title: "社区讨论升温",
        content: "多位创作者在社区分享后台数据截图，讨论流量波动的可能原因。",
      },
      {
        date: "07-27",
        title: "后台说明更新",
        content: "平台更新创作者后台，补充各来源指标的统计口径注释。",
      },
      {
        date: "07-28",
        title: "经验分享增多",
        content: "讨论转向如何根据来源结构优化选题与发布节奏。",
      },
    ],
    discussions: [
      {
        user: "日更第三年",
        role: "内容创作者",
        time: "1 周前",
        content: "指标口径说清楚之后，我才发现搜索来源一直很稳定，之前完全没注意到。",
        likes: 246,
      },
      {
        user: "数据旁白",
        role: "运营从业者",
        time: "1 周前",
        content: "与其焦虑流量，不如把来源拆开看，问题通常会更具体。",
        likes: 175,
      },
    ],
    notes: ["整理内容基于平台公开更新与创作者社区分享。", "不涉及任何平台内部规则细节。"],
  },
];

export const getEventById = (id: string): EventItem | undefined =>
  events.find((item) => item.id === id);